<template>
  <div
    ref="stageRef"
    class="relative flex h-full min-h-0 w-full select-none items-center justify-center overflow-auto"
    @pointerdown.self="emit('request-apply')"
    @wheel.ctrl.prevent="handleWheelZoom"
  >
    <div
      ref="canvasShellRef"
      class="relative origin-top-left"
      :style="{ transform: `scale(${zoomLevel})` }"
    >
      <canvas ref="canvasRef" class="block max-h-full max-w-full shadow-md" />
      <div v-if="cropBox" class="pointer-events-none absolute inset-0 select-none">
        <div
          class="absolute bg-slate-950/50"
          :style="{ left: '0px', top: '0px', width: `${canvasWidth}px`, height: `${cropBox.y}px` }"
        />
        <div
          class="absolute bg-slate-950/50"
          :style="{
            left: `${cropBox.x + cropBox.width}px`,
            top: `${cropBox.y}px`,
            width: `${canvasWidth - cropBox.x - cropBox.width}px`,
            height: `${cropBox.height}px`,
          }"
        />
        <div
          class="absolute bg-slate-950/50"
          :style="{
            left: '0px',
            top: `${cropBox.y + cropBox.height}px`,
            width: `${canvasWidth}px`,
            height: `${canvasHeight - cropBox.y - cropBox.height}px`,
          }"
        />
        <div
          class="absolute bg-slate-950/50"
          :style="{
            left: '0px',
            top: `${cropBox.y}px`,
            width: `${cropBox.x}px`,
            height: `${cropBox.height}px`,
          }"
        />
        <div
          class="pointer-events-auto absolute touch-none select-none border-2 border-white shadow-[0_0_0_1px_rgba(16,185,129,0.8)]"
          :style="{
            left: `${cropBox.x}px`,
            top: `${cropBox.y}px`,
            width: `${cropBox.width}px`,
            height: `${cropBox.height}px`,
            cursor: 'move',
          }"
          @pointerdown.stop.prevent="startCropInteraction($event, 'move')"
        >
          <button
            v-for="handle in cropHandles"
            :key="handle.key"
            type="button"
            class="absolute h-4 w-4 touch-none select-none rounded-full border-2 border-white bg-[var(--p-primary-color)] shadow-sm"
            :class="handle.class"
            :style="{ cursor: handle.cursor }"
            @pointerdown.stop.prevent="startCropInteraction($event, handle.key)"
          />
        </div>
      </div>
      <div v-if="textBoxFrame && !cropBox" class="pointer-events-none absolute inset-0 select-none">
        <div
          class="absolute touch-none select-none border-2 border-[var(--p-primary-color)] shadow-[0_0_0_1px_rgba(255,255,255,0.85)]"
          :class="isTextEditing ? 'pointer-events-none' : 'pointer-events-auto'"
          :style="{
            left: `${textBoxFrame.x}px`,
            top: `${textBoxFrame.y}px`,
            width: `${textBoxFrame.width}px`,
            height: `${textBoxFrame.height}px`,
            cursor: 'move',
            transform: `rotate(${activeTextBox?.angle ?? 0}deg)`,
            transformOrigin: 'center center',
          }"
          @pointerdown.stop.prevent="startTextInteraction($event, 'move')"
          @dblclick.stop.prevent="editActiveText"
        >
          <button
            v-if="!isTextEditing"
            type="button"
            class="absolute -top-10 left-1/2 h-5 w-5 -translate-x-1/2 touch-none select-none rounded-full border-2 border-white bg-[var(--p-primary-color)] text-xs leading-none text-white shadow-sm"
            title="Rotar"
            style="cursor: grab"
            @pointerdown.stop.prevent="startTextInteraction($event, 'rotate')"
          >
            ↻
          </button>
          <button
            v-for="handle in cropHandles"
            v-if="!isTextEditing"
            :key="handle.key"
            type="button"
            class="absolute h-4 w-4 touch-none select-none rounded-full border-2 border-white bg-[var(--p-primary-color)] shadow-sm"
            :class="handle.class"
            :style="{ cursor: handle.cursor }"
            @pointerdown.stop.prevent="startTextInteraction($event, handle.key)"
          />
        </div>
      </div>
      <div
        v-if="shapeBoxFrame && !cropBox && !isTextEditing"
        class="pointer-events-none absolute inset-0 select-none"
      >
        <div
          class="pointer-events-auto absolute touch-none select-none border-2 border-[var(--p-primary-color)] shadow-[0_0_0_1px_rgba(255,255,255,0.85)]"
          :style="{
            left: `${shapeBoxFrame.x}px`,
            top: `${shapeBoxFrame.y}px`,
            width: `${shapeBoxFrame.width}px`,
            height: `${shapeBoxFrame.height}px`,
            cursor: 'move',
            transform: `rotate(${activeShape?.angle ?? 0}deg)`,
            transformOrigin: 'center center',
          }"
          @pointerdown.stop.prevent="startShapeInteraction($event, 'move')"
        >
          <button
            type="button"
            class="absolute -top-10 left-1/2 h-5 w-5 -translate-x-1/2 touch-none select-none rounded-full border-2 border-white bg-[var(--p-primary-color)] text-xs leading-none text-white shadow-sm"
            title="Rotar"
            style="cursor: grab"
            @pointerdown.stop.prevent="startShapeInteraction($event, 'rotate')"
          >
            ↻
          </button>
          <button
            v-for="handle in cropHandles"
            :key="handle.key"
            type="button"
            class="absolute h-4 w-4 touch-none select-none rounded-full border-2 border-white bg-[var(--p-primary-color)] shadow-sm"
            :class="handle.class"
            :style="{ cursor: handle.cursor }"
            @pointerdown.stop.prevent="startShapeInteraction($event, handle.key)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as fabric from 'fabric'
import { useCanvasHistory } from '../../composables/useCanvasHistory'

type CropRatio = 'free' | '1:1' | '4:3' | '3:4' | '16:9' | '9:16' | '3:2' | '2:3' | '21:9'
type CropHandle = 'move' | 'n' | 'e' | 's' | 'w' | 'nw' | 'ne' | 'sw' | 'se'
type ShapeHandle = CropHandle | 'rotate'
type TextHandle = CropHandle | 'rotate'
type CropBox = {
  x: number
  y: number
  width: number
  height: number
}

const props = defineProps<{
  file: File
  activeTool: string
  color: string
  brushWidth: number
  fontSize: number
  fontFamily: string
  textAlign: string
  cropRatio: CropRatio
  censorMode: 'blur' | 'pixelate'
  aspectRatio?: number | null
}>()

const emit = defineEmits<{
  ready: [history: { canUndo: boolean; canRedo: boolean }]
  history: [history: { canUndo: boolean; canRedo: boolean }]
  'request-apply': []
  crop: [payload: CropPayload]
  change: [payload: CropPayload]
  'text-edit': []
  'text-style': [payload: TextStylePayload]
  'censor-style': [payload: { mode: 'blur' | 'pixelate'; level: number }]
  'selection-change': [selected: boolean]
}>()

type CropPayload = {
  left: number
  top: number
  width: number
  height: number
  ratio: CropRatio | number | null
}

type TextStylePayload = {
  color: string
  fontSize: number
  fontFamily: string
  textAlign: string
}

const canvasRef = ref<HTMLCanvasElement>()
const stageRef = ref<HTMLDivElement>()
const canvasShellRef = ref<HTMLDivElement>()
const canvas = ref<fabric.Canvas>()
const baseImage = ref<fabric.FabricImage>()
const cropRect = ref<fabric.Rect>()
const cropOverlay = ref<fabric.Rect[]>([])
const cropBox = ref<CropBox | null>(null)
const activeTextBox = ref<fabric.Textbox | null>(null)
const textBoxFrame = ref<CropBox | null>(null)
const activeShape = ref<fabric.FabricObject | null>(null)
const shapeBoxFrame = ref<CropBox | null>(null)
const censorMask = ref<fabric.Rect | null>(null)
const censorBlurLayer = ref<fabric.FabricImage | null>(null)
const censorLevel = ref(0.35)
const isTextEditing = ref(false)
const canvasWidth = ref(0)
const canvasHeight = ref(0)
const history = useCanvasHistory()
let resizeObserver: ResizeObserver | null = null
let objectUrl = ''
let lastCropSourceDataUrl = ''
let stopActivePointerInteraction: (() => void) | null = null
let restoreTextMouseMove: (() => void) | null = null
let censorSequence = 0
let isDeletingObject = false
const zoomLevel = ref(1)
const activeAspectRatio = computed(() => {
  if (typeof props.aspectRatio === 'number' && props.aspectRatio > 0) return props.aspectRatio
  if (props.cropRatio === 'free') return null
  const [width, height] = props.cropRatio.split(':').map(Number)
  if (width > 0 && height > 0) return width / height
  return null
})
const cropHandles = [
  { key: 'nw', class: '-left-2 -top-2', cursor: 'nwse-resize' },
  { key: 'n', class: 'left-1/2 -top-2 -translate-x-1/2', cursor: 'ns-resize' },
  { key: 'ne', class: '-right-2 -top-2', cursor: 'nesw-resize' },
  { key: 'e', class: '-right-2 top-1/2 -translate-y-1/2', cursor: 'ew-resize' },
  { key: 'se', class: '-bottom-2 -right-2', cursor: 'nwse-resize' },
  { key: 's', class: '-bottom-2 left-1/2 -translate-x-1/2', cursor: 'ns-resize' },
  { key: 'sw', class: '-bottom-2 -left-2', cursor: 'nesw-resize' },
  { key: 'w', class: '-left-2 top-1/2 -translate-y-1/2', cursor: 'ew-resize' },
] as const

const emitHistoryState = () => {
  emit('history', { canUndo: history.canUndo.value, canRedo: history.canRedo.value })
}

const isCropUiObject = (object: fabric.FabricObject | { get: (key: string) => unknown }) =>
  object.get('name') === 'crop-rect' || object.get('name') === 'crop-overlay'

const getCropPayload = (): CropPayload | null => {
  if (!cropBox.value) return null
  return {
    left: Math.round(cropBox.value.x),
    top: Math.round(cropBox.value.y),
    width: Math.round(cropBox.value.width),
    height: Math.round(cropBox.value.height),
    ratio: activeAspectRatio.value ?? props.cropRatio,
  }
}

const emitCropChange = () => {
  const payload = getCropPayload()
  if (!payload) return
  emit('crop', payload)
  emit('change', payload)
}

const setDrawingBrush = () => {
  if (!canvas.value) return
  canvas.value.freeDrawingBrush = new fabric.PencilBrush(canvas.value)
  canvas.value.freeDrawingBrush.color = props.color
  canvas.value.freeDrawingBrush.width = props.brushWidth
  canvas.value.freeDrawingCursor = buildBrushCursor(props.color, props.brushWidth)
}

const buildBrushCursor = (color: string, width: number) => {
  const size = Math.max(8, Math.min(width, 64))
  const canvasSize = size + 8
  const radius = size / 2
  const center = canvasSize / 2
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${canvasSize}" height="${canvasSize}" viewBox="0 0 ${canvasSize} ${canvasSize}">
      <circle cx="${center}" cy="${center}" r="${radius}" fill="${color}" fill-opacity="0.22" stroke="${color}" stroke-width="2"/>
    </svg>
  `
  const encodedSvg = encodeURIComponent(svg.replace(/\s+/g, ' ').trim())
  return `url("data:image/svg+xml,${encodedSvg}") ${center} ${center}, crosshair`
}

const fitBaseImageToCanvas = () => {
  const editor = canvas.value
  const image = baseImage.value
  if (!editor || !image?.width || !image?.height) return

  image.set({
    left: 0,
    top: 0,
    // The canvas dimensions are whole pixels. Derive each scale from those
    // dimensions so rounding never exposes the canvas background on export.
    scaleX: editor.width / image.width,
    scaleY: editor.height / image.height,
  })
  image.setCoords()
}

const fitCanvasToStage = () => {
  const editor = canvas.value
  const stage = stageRef.value
  const image = baseImage.value
  if (!editor || !stage || !image) return

  const maxWidth = Math.max(stage.clientWidth - 32, 320)
  const maxHeight = Math.max(stage.clientHeight - 32, 240)
  const imageWidth = image.width || 1
  const imageHeight = image.height || 1
  const scale = Math.min(maxWidth / imageWidth, maxHeight / imageHeight, 1)
  const width = Math.round(imageWidth * scale)
  const height = Math.round(imageHeight * scale)

  editor.setDimensions({ width, height })
  canvasWidth.value = width
  canvasHeight.value = height
  fitBaseImageToCanvas()
  editor.calcOffset()
  editor.requestRenderAll()
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const normalizeCropBox = (box: CropBox): CropBox => {
  const minWidth = Math.min(2, canvasWidth.value)
  const minHeight = Math.min(2, canvasHeight.value)
  const width = clamp(box.width, minWidth, canvasWidth.value)
  const height = clamp(box.height, minHeight, canvasHeight.value)
  return {
    x: clamp(box.x, 0, Math.max(canvasWidth.value - width, 0)),
    y: clamp(box.y, 0, Math.max(canvasHeight.value - height, 0)),
    width,
    height,
  }
}

const syncTextBoxFrame = () => {
  const text = activeTextBox.value
  if (!text || cropBox.value) {
    textBoxFrame.value = null
    return
  }

  const width = Math.max(text.getScaledWidth(), 80)
  const height = Math.max(text.getScaledHeight(), 32)
  const center = text.getCenterPoint()
  textBoxFrame.value = normalizeCropBox({
    x: center.x - width / 2,
    y: center.y - height / 2,
    width,
    height,
  })
}

const isResizableShape = (object: fabric.FabricObject | null) =>
  Boolean(
    object &&
      (object.get('name') === 'editable-shape' ||
        object.get('name') === 'censor-mask' ||
        object.get('isCensorMask')),
  )

const syncCensorBlur = () => {
  const mask = censorMask.value
  const blurLayer = censorBlurLayer.value
  if (!mask || !blurLayer) return

  const clipPath = blurLayer.clipPath as fabric.Rect
  clipPath.set({
    left: mask.left,
    top: mask.top,
    width: mask.width,
    height: mask.height,
    scaleX: mask.scaleX,
    scaleY: mask.scaleY,
    angle: mask.angle,
  })
  clipPath.setCoords()
  blurLayer.setCoords()
}

const syncShapeBoxFrame = () => {
  const shape = activeShape.value
  if (
    !shape ||
    !isResizableShape(shape as unknown as fabric.FabricObject) ||
    cropBox.value ||
    isTextEditing.value
  ) {
    shapeBoxFrame.value = null
    return
  }

  const width = Math.max(shape.getScaledWidth(), 24)
  const height = Math.max(shape.getScaledHeight(), 24)
  const center = shape.getCenterPoint()
  shapeBoxFrame.value = normalizeCropBox({
    x: center.x - width / 2,
    y: center.y - height / 2,
    width,
    height,
  })
}

const getTextStylePayload = (text: fabric.Textbox): TextStylePayload => {
  const fill = text.fill
  const fontFamily = text.fontFamily

  return {
    color: typeof fill === 'string' ? fill : props.color,
    fontSize: Math.round(Number(text.fontSize || props.fontSize)),
    fontFamily: typeof fontFamily === 'string' ? fontFamily : props.fontFamily,
    textAlign: typeof text.textAlign === 'string' ? text.textAlign : props.textAlign,
  }
}

const setActiveTextBox = (target?: fabric.FabricObject | null) => {
  activeShape.value = null
  shapeBoxFrame.value = null
  activeTextBox.value = target instanceof fabric.Textbox ? target : null
  if (activeTextBox.value && !activeTextBox.value.isEditing) {
    activeTextBox.value.set({ editable: false })
  }
  syncTextBoxFrame()
  if (activeTextBox.value) {
    emit('text-style', getTextStylePayload(activeTextBox.value as fabric.Textbox))
  }
}

const setActiveCanvasObject = (target?: fabric.FabricObject | null) => {
  const editor = canvas.value
  if (
    target &&
    (target === baseImage.value || target.get('name') === 'base-image')
  ) {
    editor?.discardActiveObject()
    return
  }

  if (target && isCensorMaskObject(target)) {
    target.set({ isCensorMask: true })
    censorMask.value = target as fabric.Rect
    censorBlurLayer.value = editor?.getObjects().find(
      (object) => object.get('censorId') === target.get('censorId'),
    ) as fabric.FabricImage | null
    const level = Number(target.get('censorLevel'))
    if (Number.isFinite(level)) censorLevel.value = level
    const mode = target.get('censorMode')
    if (mode === 'blur' || mode === 'pixelate') {
      emit('censor-style', { mode, level: censorLevel.value })
    }
  }

  setActiveTextBox(target)
  const object = target ? (target as unknown as fabric.FabricObject) : null
  if (isResizableShape(object)) {
    activeShape.value = object
    syncShapeBoxFrame()
  }
}

const buildInitialCropBox = () => {
  cropBox.value = normalizeCropBox({
    x: 0,
    y: 0,
    width: canvasWidth.value,
    height: canvasHeight.value,
  })
  emitCropChange()
}

const resizeCropBox = (startBox: CropBox, handle: CropHandle, dx: number, dy: number) => {
  if (handle === 'move') {
    return normalizeCropBox({
      ...startBox,
      x: startBox.x + dx,
      y: startBox.y + dy,
    })
  }

  const minSize = 8 //Tamñano minimo del rezise
  let left = startBox.x
  let top = startBox.y
  let right = startBox.x + startBox.width
  let bottom = startBox.y + startBox.height

  if (handle.includes('w')) left = clamp(startBox.x + dx, 0, right - minSize)
  if (handle.includes('e')) right = clamp(right + dx, left + minSize, canvasWidth.value)
  if (handle.includes('n')) top = clamp(startBox.y + dy, 0, bottom - minSize)
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + minSize, canvasHeight.value)

  if (activeAspectRatio.value && handle.length === 2) {
    const ratio = activeAspectRatio.value
    let width = right - left
    let height = width / ratio

    if (height > bottom - top) {
      height = bottom - top
      width = height * ratio
    }

    if (handle.includes('w')) left = right - width
    else right = left + width

    if (handle.includes('n')) top = bottom - height
    else bottom = top + height

    if (left < 0) {
      left = 0
      right = width
    }
    if (right > canvasWidth.value) {
      right = canvasWidth.value
      left = right - width
    }
    if (top < 0) {
      top = 0
      bottom = height
    }
    if (bottom > canvasHeight.value) {
      bottom = canvasHeight.value
      top = bottom - height
    }
  }

  return normalizeCropBox({
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  })
}

const startCropInteraction = (event: PointerEvent, handle: CropHandle) => {
  if (!cropBox.value || !canvasShellRef.value) return
  if (event.button !== 0) return
  stopActivePointerInteraction?.()
  document.getSelection()?.removeAllRanges()
  ;(event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId)

  const startBox = { ...cropBox.value }
  const startX = event.clientX
  const startY = event.clientY

  const handlePointerMove = (moveEvent: PointerEvent) => {
    document.getSelection()?.removeAllRanges()
    const dx = (moveEvent.clientX - startX) / zoomLevel.value
    const dy = (moveEvent.clientY - startY) / zoomLevel.value
    cropBox.value = resizeCropBox(startBox, handle, dx, dy)
    emitCropChange()
  }

  const stopInteraction = () => {
    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', stopInteraction)
    window.removeEventListener('pointercancel', stopInteraction)
    if (stopActivePointerInteraction === stopInteraction) stopActivePointerInteraction = null
  }

  stopActivePointerInteraction = stopInteraction
  window.addEventListener('pointermove', handlePointerMove)
  window.addEventListener('pointerup', stopInteraction)
  window.addEventListener('pointercancel', stopInteraction)
}

const applyTextFrame = (frame: CropBox) => {
  const editor = canvas.value
  const text = activeTextBox.value
  if (!editor || !text) return

  const width = Math.max(frame.width, 80)

  text.set({ width, scaleX: 1, scaleY: 1 })
  text.setPositionByOrigin(
    new fabric.Point(frame.x + frame.width / 2, frame.y + frame.height / 2),
    'center',
    'center',
  )
  text.initDimensions()
  text.setCoords()
  textBoxFrame.value = normalizeCropBox({
    ...frame,
    height: Math.max(frame.height, text.getScaledHeight()),
  })
  editor.setActiveObject(text as never)
  editor.requestRenderAll()
}

const startTextInteraction = (event: PointerEvent, handle: TextHandle) => {
  const editor = canvas.value
  if (
    event.button !== 0 ||
    isTextEditing.value ||
    !editor ||
    !textBoxFrame.value ||
    !activeTextBox.value
  )
    return
  stopActivePointerInteraction?.()
  document.getSelection()?.removeAllRanges()
  ;(event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId)
  activeTextBox.value.exitEditing()
  activeTextBox.value.set({ editable: false })

  const startFrame = { ...textBoxFrame.value }
  const startX = event.clientX
  const startY = event.clientY
  const rotationCenter = new fabric.Point(
    startFrame.x + startFrame.width / 2,
    startFrame.y + startFrame.height / 2,
  )
  const startPointer = editor.getScenePoint(event)
  const startPointerAngle =
    (Math.atan2(startPointer.y - rotationCenter.y, startPointer.x - rotationCenter.x) * 180) /
    Math.PI
  const startAngle = activeTextBox.value.angle ?? 0

  const handlePointerMove = (moveEvent: PointerEvent) => {
    document.getSelection()?.removeAllRanges()
    if (handle === 'rotate') {
      const pointer = editor.getScenePoint(moveEvent)
      const pointerAngle =
        (Math.atan2(pointer.y - rotationCenter.y, pointer.x - rotationCenter.x) * 180) / Math.PI
      activeTextBox.value?.set({ angle: startAngle + pointerAngle - startPointerAngle })
      activeTextBox.value?.setPositionByOrigin(rotationCenter, 'center', 'center')
      activeTextBox.value?.setCoords()
      syncTextBoxFrame()
      editor.requestRenderAll()
      return
    }
    const dx = (moveEvent.clientX - startX) / zoomLevel.value
    const dy = (moveEvent.clientY - startY) / zoomLevel.value
    const nextFrame = resizeCropBox(startFrame, handle, dx, dy)
    applyTextFrame(nextFrame)
  }

  const stopInteraction = () => {
    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', stopInteraction)
    window.removeEventListener('pointercancel', stopInteraction)
    if (stopActivePointerInteraction === stopInteraction) stopActivePointerInteraction = null
    if (canvas.value) {
      history.save(canvas.value)
      emitHistoryState()
    }
  }

  stopActivePointerInteraction = stopInteraction
  window.addEventListener('pointermove', handlePointerMove)
  window.addEventListener('pointerup', stopInteraction)
  window.addEventListener('pointercancel', stopInteraction)
}

const applyShapeFrame = (frame: CropBox) => {
  const editor = canvas.value
  const shape = activeShape.value
  if (!editor || !shape) return

  const width = Math.max(shape.width || 1, 1)
  const height = Math.max(shape.height || 1, 1)
  shape.set({
    scaleX: Math.max(frame.width / width, 0.05),
    scaleY: Math.max(frame.height / height, 0.05),
  })
  shape.setPositionByOrigin(
    new fabric.Point(frame.x + frame.width / 2, frame.y + frame.height / 2),
    'center',
    'center',
  )
  shape.setCoords()
  syncCensorBlur()
  syncShapeBoxFrame()
  editor.requestRenderAll()
}

const moveShapeFrame = (start: CropBox, dx: number, dy: number) => {
  const editor = canvas.value
  const shape = activeShape.value
  if (!editor || !shape) return

  const startCenter = new fabric.Point(
    start.x + start.width / 2,
    start.y + start.height / 2,
  )
  shape.setPositionByOrigin(
    new fabric.Point(startCenter.x + dx, startCenter.y + dy),
    'center',
    'center',
  )
  shape.setCoords()
  syncCensorBlur()
  syncShapeBoxFrame()
  editor.requestRenderAll()
}

const resizeShapeFrame = (
  start: CropBox,
  handle: CropHandle,
  dx: number,
  dy: number,
  keepAspect: boolean,
) => {
  const next = resizeCropBox(start, handle, dx, dy)
  if (!keepAspect || handle === 'move') return next

  const aspect = start.width / Math.max(start.height, 1)
  let width = next.width
  let height = next.height

  if (handle === 'e' || handle === 'w' || handle.includes('e') || handle.includes('w')) {
    height = width / aspect
  } else {
    width = height * aspect
  }

  let x = next.x
  let y = next.y
  if (handle.includes('w')) x = start.x + start.width - width
  else if (handle === 'e') x = start.x
  else if (!handle.includes('e')) x = start.x + (start.width - width) / 2

  if (handle.includes('n')) y = start.y + start.height - height
  else if (handle === 's') y = start.y
  else if (!handle.includes('s')) y = start.y + (start.height - height) / 2

  return normalizeCropBox({ x, y, width, height })
}

const rotateShape = (
  shape: fabric.FabricObject,
  startAngle: number,
  startPointerAngle: number,
  rotationCenter: fabric.Point,
  editor: fabric.Canvas,
  event: PointerEvent,
) => {
  const pointer = editor.getScenePoint(event)
  const pointerAngle =
    (Math.atan2(pointer.y - rotationCenter.y, pointer.x - rotationCenter.x) * 180) / Math.PI
  shape.set({ angle: startAngle + pointerAngle - startPointerAngle })
  shape.setPositionByOrigin(rotationCenter, 'center', 'center')
  shape.setCoords()
  syncCensorBlur()
  syncShapeBoxFrame()
  canvas.value?.requestRenderAll()
}

const startShapeInteraction = (event: PointerEvent, handle: ShapeHandle) => {
  const editor = canvas.value
  if (event.button !== 0 || !editor || !shapeBoxFrame.value || !activeShape.value) return
  stopActivePointerInteraction?.()
  document.getSelection()?.removeAllRanges()
  ;(event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId)

  const startFrame = { ...shapeBoxFrame.value }
  const startX = event.clientX
  const startY = event.clientY
  const startAngle = activeShape.value.angle ?? 0
  const rotationCenter = new fabric.Point(
    startFrame.x + startFrame.width / 2,
    startFrame.y + startFrame.height / 2,
  )
  const startPointer = editor.getScenePoint(event)
  const startPointerAngle =
    (Math.atan2(startPointer.y - rotationCenter.y, startPointer.x - rotationCenter.x) * 180) /
    Math.PI

  const handlePointerMove = (moveEvent: PointerEvent) => {
    document.getSelection()?.removeAllRanges()
    if (handle === 'rotate') {
      rotateShape(
        activeShape.value as unknown as fabric.FabricObject,
        startAngle,
        startPointerAngle,
        rotationCenter,
        editor,
        moveEvent,
      )
      return
    }
    const dx = (moveEvent.clientX - startX) / zoomLevel.value
    const dy = (moveEvent.clientY - startY) / zoomLevel.value
    if (handle === 'move') {
      moveShapeFrame(startFrame, dx, dy)
    } else {
      applyShapeFrame(resizeShapeFrame(startFrame, handle, dx, dy, moveEvent.shiftKey))
    }
  }

  const stopInteraction = () => {
    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', stopInteraction)
    window.removeEventListener('pointercancel', stopInteraction)
    if (stopActivePointerInteraction === stopInteraction) stopActivePointerInteraction = null
    if (canvas.value) {
      history.save(canvas.value)
      emitHistoryState()
    }
  }

  stopActivePointerInteraction = stopInteraction
  window.addEventListener('pointermove', handlePointerMove)
  window.addEventListener('pointerup', stopInteraction)
  window.addEventListener('pointercancel', stopInteraction)
}

const editActiveText = () => {
  const editor = canvas.value
  const text = activeTextBox.value
  if (!editor || !text) return
  stopActivePointerInteraction?.()
  bindTextEditingEvents(text as fabric.Textbox, editor)
  isTextEditing.value = true
  textBoxFrame.value = null
  text.set({ editable: true, selectable: true })
  editor.selection = false
  editor.setActiveObject(text as never)
  text.enterEditing()
  text.hiddenTextarea?.focus()
  emit('text-edit')
  editor.requestRenderAll()
}

const boundTextEditingObjects = new WeakSet<fabric.Textbox>()

const bindTextEditingEvents = (text: fabric.Textbox, editor: fabric.Canvas) => {
  if (boundTextEditingObjects.has(text)) return
  boundTextEditingObjects.add(text)

  text.on('editing:entered', () => {
    isTextEditing.value = true
    editor.selection = false
    text.set({ lockMovementX: true, lockMovementY: true })
    text.set({ hoverCursor: 'auto', moveCursor: 'auto' } as never)
    editor.upperCanvasEl.classList.add('text-editing-cursor')
    syncTextBoxFrame()

    if (!restoreTextMouseMove) {
      const textEditingManager = editor.textEditingManager
      const originalOnMouseMove = textEditingManager.onMouseMove.bind(textEditingManager)
      textEditingManager.onMouseMove = (event) => {
        const pointerEvent = event as MouseEvent | PointerEvent
        if (pointerEvent.buttons !== 0) originalOnMouseMove(event)
      }
      restoreTextMouseMove = () => {
        textEditingManager.onMouseMove = originalOnMouseMove
        restoreTextMouseMove = null
      }
    }
  })
  text.on('editing:exited', () => {
    isTextEditing.value = false
    editor.selection = true
    text.set({ editable: false, lockMovementX: false, lockMovementY: false })
    text.set({ hoverCursor: 'move', moveCursor: 'move' } as never)
    editor.upperCanvasEl.classList.remove('text-editing-cursor')
    restoreTextMouseMove?.()
    history.save(editor)
    emitHistoryState()
    // Keep the frame available after blur so the text can be resized immediately.
    editor.setActiveObject(text as never)
    setActiveTextBox(text)
    editor.requestRenderAll()
  })
}

const setZoomLevel = (nextZoom: number) => {
  zoomLevel.value = Math.min(Math.max(nextZoom, 1), 4)
  requestAnimationFrame(() => canvas.value?.calcOffset())
}

const zoomIn = () => setZoomLevel(zoomLevel.value + 0.15)
const zoomOut = () => setZoomLevel(zoomLevel.value - 0.15)
const resetZoom = () => setZoomLevel(1)

const handleWheelZoom = (event: WheelEvent) => {
  const direction = event.deltaY > 0 ? -1 : 1
  setZoomLevel(zoomLevel.value + direction * 0.12)
}

const updateCropOverlay = () => {
  const editor = canvas.value
  const rect = cropRect.value
  if (!editor || !rect) return

  const left = rect.left ?? 0
  const top = rect.top ?? 0
  const width = rect.getScaledWidth()
  const height = rect.getScaledHeight()
  const canvasWidth = editor.width
  const canvasHeight = editor.height
  const [topShade, rightShade, bottomShade, leftShade] = cropOverlay.value

  topShade?.set({ left: 0, top: 0, width: canvasWidth, height: top })
  rightShade?.set({
    left: left + width,
    top,
    width: Math.max(canvasWidth - left - width, 0),
    height,
  })
  bottomShade?.set({
    left: 0,
    top: top + height,
    width: canvasWidth,
    height: Math.max(canvasHeight - top - height, 0),
  })
  leftShade?.set({ left: 0, top, width: left, height })
  cropOverlay.value.forEach((shade) => shade.setCoords())
}

const createCropOverlay = () => {
  const editor = canvas.value
  if (!editor || cropOverlay.value.length) return

  cropOverlay.value = Array.from(
    { length: 4 },
    () =>
      new fabric.Rect({
        left: 0,
        top: 0,
        width: 1,
        height: 1,
        fill: 'rgba(2, 6, 23, 0.48)',
        selectable: false,
        evented: false,
        excludeFromExport: true,
        name: 'crop-overlay',
      }),
  )
  cropOverlay.value.forEach((shade) => editor.add(shade as never))
}

const removeCropUi = () => {
  const editor = canvas.value
  if (!editor) return
  editor.uniformScaling = false
  cropOverlay.value.forEach((shade) => editor.remove(shade as never))
  cropOverlay.value = []
  if (cropRect.value) editor.remove(cropRect.value as never)
  cropRect.value = undefined
  cropBox.value = null
}

const loadFlattenedImage = async (dataUrl: string) => {
  const editor = canvas.value
  if (!editor || !dataUrl) return

  const image = await fabric.FabricImage.fromURL(dataUrl)
  editor.getObjects().forEach((object) => editor.remove(object))
  cropRect.value = undefined
  cropOverlay.value = []
  lockBaseImage(image)
  baseImage.value = image
  editor.add(image)
  fitCanvasToStage()
  editor.discardActiveObject()
  editor.requestRenderAll()
}

const restoreLastCropSource = async () => {
  if (!lastCropSourceDataUrl) return false
  await loadFlattenedImage(lastCropSourceDataUrl)
  buildInitialCropBox()
  return true
}

const rotateDataUrl = (dataUrl: string, degrees: 90 | -90) =>
  new Promise<string>((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      const output = document.createElement('canvas')
      output.width = image.naturalHeight
      output.height = image.naturalWidth
      const context = output.getContext('2d')
      if (!context) {
        reject(new Error('Canvas context unavailable'))
        return
      }

      context.translate(output.width / 2, output.height / 2)
      context.rotate((degrees * Math.PI) / 180)
      context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2)
      resolve(output.toDataURL('image/png'))
    }
    image.onerror = () => reject(new Error('Image rotation failed'))
    image.src = dataUrl
  })

const rotateContent = async (degrees: 90 | -90) => {
  const source =
    cropBox.value && lastCropSourceDataUrl ? lastCropSourceDataUrl : exportCanvasDataUrl()
  if (!source) return

  const shouldKeepCropActive = Boolean(cropBox.value)
  const rotatedDataUrl = await rotateDataUrl(source, degrees)
  lastCropSourceDataUrl = ''
  removeCropUi()
  await loadFlattenedImage(rotatedDataUrl)
  history.reset(canvas.value!)
  emitHistoryState()
  if (shouldKeepCropActive) buildInitialCropBox()
}

const rotateLeft = () => rotateContent(-90)
const rotateRight = () => rotateContent(90)

const undoCrop = async () => {
  if (cropBox.value) {
    buildInitialCropBox()
    return
  }

  if (await restoreLastCropSource()) {
    lastCropSourceDataUrl = ''
    history.reset(canvas.value!)
    emitHistoryState()
  }
}

const deleteActiveObject = () => {
  const editor = canvas.value
  const active = editor?.getActiveObject()
  if (!editor || !active || active === baseImage.value || isCropUiObject(active)) return

  const objectsToRemove = [active]
  if (isCensorMaskObject(active)) {
    const censorId = active.get('censorId')
    const blurLayer = editor.getObjects().find(
      (object) => object !== active && object.get('censorId') === censorId,
    )
    if (blurLayer) objectsToRemove.push(blurLayer)
  }

  isDeletingObject = true
  editor.discardActiveObject()
  objectsToRemove.forEach((object) => editor.remove(object))
  isDeletingObject = false
  syncCanvasRefs()
  editor.requestRenderAll()
  history.save(editor)
  emitHistoryState()
}

const handleEditorKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Delete' && event.key !== 'Backspace') return
  if (isTextEditing.value) return

  const target = event.target as HTMLElement | null
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target?.isContentEditable
  ) {
    return
  }

  const active = canvas.value?.getActiveObject()
  if (!active || active === baseImage.value || isCropUiObject(active)) return
  event.preventDefault()
  deleteActiveObject()
}

const initializeCanvas = async () => {
  if (!canvasRef.value) return
  disposeCanvas()
  objectUrl = URL.createObjectURL(props.file)

  const editor = new fabric.Canvas(canvasRef.value, {
    // The surrounding stage supplies the dark editor background. Keeping the
    // Fabric canvas transparent prevents a fallback color from being exported.
    backgroundColor: 'transparent',
    preserveObjectStacking: true,
    selection: true,
    uniformScaling: false,
    uniScaleKey: undefined,
  })
  canvas.value = editor

  const image = await fabric.FabricImage.fromURL(objectUrl, { crossOrigin: 'anonymous' })
  lockBaseImage(image)
  baseImage.value = image
  editor.add(image)
  editor.sendObjectToBack(image)
  fitCanvasToStage()
  setDrawingBrush()

  editor.on('object:added', () => {
    history.save(editor)
    emitHistoryState()
  })
  editor.on('object:modified', () => {
    syncTextBoxFrame()
    syncShapeBoxFrame()
    syncCensorBlur()
    history.save(editor)
    emitHistoryState()
  })
  editor.on('object:removed', () => {
    if (isDeletingObject) return
    history.save(editor)
    emitHistoryState()
  })
  editor.on('path:created', (event) => {
    event.path?.set({ isDrawingPath: true, name: 'drawing-path' })
    if (event.path) lockDrawingPath(event.path)
    history.save(editor)
    emitHistoryState()
  })
  window.addEventListener('keydown', handleEditorKeyDown)
  editor.on('mouse:dblclick', (event) => {
    if (!(event.target instanceof fabric.Textbox)) return
    setActiveTextBox(event.target)
    editActiveText()
  })
  editor.on('text:changed', () => {
    syncTextBoxFrame()
    history.save(editor)
    emitHistoryState()
  })
  editor.on('object:scaling', (event) => {
    if (!(event.target instanceof fabric.Textbox)) return
    resizeTextbox(event.target)
  })
  editor.on('selection:created', (event) => {
    const target = event.selected?.[0] ?? null
    setActiveCanvasObject(target)
    emit('selection-change', Boolean(target && !isCensorBlurLayer(target, baseImage.value) && !isDrawingPathObject(target)))
  })
  editor.on('selection:updated', (event) => {
    const target = event.selected?.[0] ?? null
    setActiveCanvasObject(target)
    emit('selection-change', Boolean(target && !isCensorBlurLayer(target, baseImage.value) && !isDrawingPathObject(target)))
  })
  editor.on('selection:cleared', () => {
    setActiveCanvasObject(null)
    emit('selection-change', false)
  })
  editor.on('object:moving', (event) => {
    if (isTextEditing.value) {
      event.target?.set({ left: event.target.left, top: event.target.top })
      return
    }
    if (event.target === (activeTextBox.value as unknown)) syncTextBoxFrame()
    if (event.target === (activeShape.value as unknown)) syncShapeBoxFrame()
  })

  history.reset(editor)
  emit('ready', { canUndo: history.canUndo.value, canRedo: history.canRedo.value })
  emitHistoryState()
}

const lockBaseImage = (image: fabric.FabricImage) => {
  image.set({
    selectable: false,
    evented: false,
    hasControls: false,
    hasBorders: false,
    lockMovementX: true,
    lockMovementY: true,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    hoverCursor: 'default',
    moveCursor: 'default',
    name: 'base-image',
  })
}

const isCensorMaskObject = (object?: fabric.FabricObject | null) =>
  Boolean(
    object &&
      !(object instanceof fabric.FabricImage) &&
      (object.get('isCensorMask') ||
        object.get('name') === 'censor-mask' ||
        object.get('censorId') ||
        (object.type === 'rect' &&
          object.get('excludeFromExport') &&
          object.get('name') !== 'crop-rect' &&
          object.get('name') !== 'crop-overlay')),
  )

const isDrawingPathObject = (object: fabric.FabricObject) =>
  Boolean(
    object.get('isDrawingPath') ||
      object.get('name') === 'drawing-path' ||
      (object.type === 'path' && object.get('name') !== 'editable-shape'),
  )

const isCensorBlurLayer = (
  object: fabric.FabricObject,
  image: fabric.FabricImage | undefined,
) =>
  object !== image &&
  (object.get('name') === 'censor-blur' ||
    (object instanceof fabric.FabricImage && Boolean(object.get('clipPath'))))

const lockCensorBlurLayer = (image: fabric.FabricImage) => {
  image.set({
    selectable: false,
    evented: false,
    hasControls: false,
    hasBorders: false,
    lockMovementX: true,
    lockMovementY: true,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    name: 'censor-blur',
  })
}

const lockDrawingPath = (path: fabric.FabricObject) => {
  path.set({
    selectable: false,
    evented: false,
    hasControls: false,
    hasBorders: false,
    lockMovementX: true,
    lockMovementY: true,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
  })
}

const enableObjectControls = (object: fabric.FabricObject) => {
  object.set({
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    lockMovementX: false,
    lockMovementY: false,
    lockScalingX: false,
    lockScalingY: false,
    lockRotation: false,
    lockScalingFlip: true,
    cornerColor: props.color,
    cornerStrokeColor: '#ffffff',
    cornerSize: 14,
    cornerStyle: 'circle',
    transparentCorners: false,
  })

  if (object instanceof fabric.Textbox) {
    object.set({
      editable: false,
      hasControls: false,
      hasBorders: false,
      minWidth: 80,
      lockScalingY: false,
      lockUniScaling: false,
      noScaleCache: false,
      borderColor: 'transparent',
      cornerSize: 0,
    } as Partial<fabric.Textbox>)
  } else if (isResizableShape(object)) {
    object.set({
      hasControls: false,
      hasBorders: false,
    })
  }
}

const resizeTextbox = (text: fabric.Textbox) => {
  const editor = canvas.value
  if (!editor) return

  const minWidth = 80
  const maxWidth = Math.max(editor.width - (text.left ?? 0), minWidth)
  const nextWidth = Math.min(Math.max(text.getScaledWidth(), minWidth), maxWidth)
  const nextFontSize = Math.min(
    Math.max((text.fontSize || props.fontSize) * (text.scaleY || 1), 8),
    160,
  )

  text.set({
    width: nextWidth,
    fontSize: nextFontSize,
    scaleX: 1,
    scaleY: 1,
  })
  text.initDimensions()
  text.setCoords()
  editor.requestRenderAll()
}

const syncCanvasRefs = () => {
  const editor = canvas.value
  if (!editor) return

  // A Fabric history snapshot includes background and object scales. Those
  // values belong to the previous display state, never to the exported image.
  editor.backgroundColor = 'transparent'
  canvasWidth.value = editor.width
  canvasHeight.value = editor.height
  const objects = editor.getObjects()
  const active = editor.getActiveObject()
  censorMask.value = (isCensorMaskObject(active)
    ? active
    : objects.find((object) => isCensorMaskObject(object))) as fabric.Rect | null
  censorBlurLayer.value = objects.find(
    (object) => object.get('censorId') === censorMask.value?.get('censorId'),
  ) as fabric.FabricImage | null
  cropRect.value = objects.find((object) => object.get('name') === 'crop-rect') as
    | fabric.Rect
    | undefined
  cropOverlay.value = objects.filter(
    (object) => object.get('name') === 'crop-overlay',
  ) as fabric.Rect[]
  // Custom properties can be lost by Fabric while hydrating a history
  // snapshot. Fall back to the image object itself so it is never treated as
  // an editable annotation after undo/redo.
  baseImage.value = (objects.find((object) => object.get('name') === 'base-image') ??
    objects.find((object) => object instanceof fabric.FabricImage)) as
    | fabric.FabricImage
    | undefined
  if (baseImage.value) {
    lockBaseImage(baseImage.value)
    fitBaseImageToCanvas()
  }
  objects.forEach((object) => {
    if (isCensorBlurLayer(object, baseImage.value)) {
      lockCensorBlurLayer(object as fabric.FabricImage)
      return
    }
    if (isCensorMaskObject(object)) {
      object.set({
        name: 'censor-mask',
        isCensorMask: true,
        selectable: true,
        evented: true,
        lockMovementX: false,
        lockMovementY: false,
        lockScalingX: false,
        lockScalingY: false,
        lockRotation: false,
      })
      enableObjectControls(object)
      return
    }
    if (isDrawingPathObject(object)) {
      lockDrawingPath(object)
      return
    }
    if (
      object !== baseImage.value &&
      !isCropUiObject(object)
    ) {
      enableObjectControls(object)
      if (object instanceof fabric.Textbox) bindTextEditingEvents(object, editor)
    }
  })
  if (baseImage.value) editor.sendObjectToBack(baseImage.value)
  setDrawingBrush()
  editor.discardActiveObject()
  editor.calcOffset()
  editor.requestRenderAll()
}

const addObject = (object: fabric.FabricObject) => {
  const editor = canvas.value
  if (!editor) return
  editor.isDrawingMode = false
  object.set({ name: 'editable-shape' })
  enableObjectControls(object)
  object.set({ left: 80, top: 80 })
  editor.add(object)
  editor.setActiveObject(object)
  editor.calcOffset()
  editor.requestRenderAll()
}

const addText = () => {
  const editor = canvas.value
  if (!editor) return

  const text = new fabric.Textbox('Texto', {
    left: Math.max(editor.width / 2 - 120, 16),
    top: Math.max(editor.height / 2 - props.fontSize, 16),
    width: Math.min(240, Math.max(editor.width - 32, 120)),
    fill: props.color,
    fontSize: props.fontSize,
    fontFamily: props.fontFamily,
    textAlign: props.textAlign,
    editable: false,
    splitByGrapheme: false,
    minWidth: 80,
    editingBorderColor: props.color,
    cursorColor: props.color,
    backgroundColor: 'rgba(255, 255, 255, 0.01)',
    padding: 4,
  })
  bindTextEditingEvents(text, editor)

  editor.isDrawingMode = false
  enableObjectControls(text)
  editor.add(text)
  editor.setActiveObject(text)
  setActiveTextBox(text)
  editor.requestRenderAll()

  requestAnimationFrame(() => {
    text.set({ editable: true })
    text.enterEditing()
    text.selectAll()
    text.hiddenTextarea?.focus()
  })
}

const getActiveText = () => {
  const active = canvas.value?.getActiveObject()
  if (
    active instanceof fabric.IText ||
    active instanceof fabric.Textbox ||
    active instanceof fabric.Text
  ) {
    return active
  }
  return null
}

const applyTextStyleToSelection = () => {
  const editor = canvas.value
  const activeText = getActiveText()
  if (!editor || !activeText) return

  activeText.set({
    fill: props.color,
    fontSize: props.fontSize,
    fontFamily: props.fontFamily,
    textAlign: props.textAlign,
  })
  editor.requestRenderAll()
  history.save(editor)
  emitHistoryState()
}

const addRect = () => {
  addObject(
    new fabric.Rect({
      width: 160,
      height: 100,
      fill: 'transparent',
      stroke: props.color,
      strokeWidth: 4,
    }),
  )
}

const addCircle = () => {
  addObject(
    new fabric.Circle({
      radius: 56,
      fill: 'transparent',
      stroke: props.color,
      strokeWidth: 4,
    }),
  )
}

const addLine = () => {
  addObject(
    new fabric.Line([40, 40, 220, 40], {
      stroke: props.color,
      strokeWidth: 4,
    }),
  )
}

const addArrow = () => {
  addObject(
    new fabric.Path('M 0 0 L 180 0 M 158 -16 L 180 0 L 158 16', {
      stroke: props.color,
      strokeWidth: 4,
      fill: '',
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
      angle: -25,
    }),
  )
}

const addEmoji = (emoji: string) => {
  addObject(new fabric.Text(emoji, { fontSize: 56 }))
}

const keepCropRectInsideCanvas = (rect: fabric.Rect) => {
  const editor = canvas.value
  if (!editor) return

  const minSize = 48
  let scaleX = rect.scaleX || 1
  let scaleY = rect.scaleY || 1
  let width = rect.getScaledWidth()
  let height = rect.getScaledHeight()

  if (width < minSize) {
    scaleX = minSize / (rect.width || 1)
    width = minSize
  }

  if (height < minSize) {
    scaleY = minSize / (rect.height || 1)
    height = minSize
  }

  if (width > editor.width) {
    scaleX = editor.width / (rect.width || 1)
    width = editor.width
  }

  if (height > editor.height) {
    scaleY = editor.height / (rect.height || 1)
    height = editor.height
  }

  const left = Math.min(Math.max(rect.left ?? 0, 0), Math.max(editor.width - width, 0))
  const top = Math.min(Math.max(rect.top ?? 0, 0), Math.max(editor.height - height, 0))

  rect.set({
    left,
    top,
    scaleX,
    scaleY,
  })
  rect.setCoords()
  updateCropOverlay()
  emitCropChange()
}

const fitSizeToAspectRatio = (width: number, height: number, ratio: number) => {
  let nextWidth = width
  let nextHeight = width / ratio

  if (nextHeight > height) {
    nextHeight = height
    nextWidth = height * ratio
  }

  return {
    width: Math.max(nextWidth, 1),
    height: Math.max(nextHeight, 1),
  }
}

const ensureCropRect = async () => {
  const editor = canvas.value
  if (!editor) return
  setActiveTextBox(null)
  if (cropBox.value) {
    return
  }
  if (lastCropSourceDataUrl && (await restoreLastCropSource())) {
    editor.calcOffset()
    editor.requestRenderAll()
    emitCropChange()
    return
  }
  editor.discardActiveObject()
  editor.isDrawingMode = false
  buildInitialCropBox()
  editor.calcOffset()
  editor.requestRenderAll()
}

const applyCropRatio = () => {
  if (!cropBox.value) return

  if (!activeAspectRatio.value) {
    cropBox.value = normalizeCropBox(cropBox.value)
    emitCropChange()
    return
  }
  const ratio = activeAspectRatio.value
  const size = fitSizeToAspectRatio(cropBox.value.width, cropBox.value.height, ratio)
  cropBox.value = normalizeCropBox({
    x: cropBox.value.x,
    y: cropBox.value.y,
    width: size.width,
    height: size.height,
  })
  emitCropChange()
}

const getExportBox = (): CropBox | null => {
  const editor = canvas.value
  if (!editor) return null
  return cropBox.value ?? { x: 0, y: 0, width: editor.width, height: editor.height }
}

const getExportPixelSize = (box: CropBox) => {
  const editor = canvas.value
  const image = baseImage.value
  if (!editor || !image?.width || !image?.height || !editor.width || !editor.height) {
    return {
      width: Math.max(1, Math.round(box.width)),
      height: Math.max(1, Math.round(box.height)),
    }
  }

  // The canvas is a rounded, display-sized representation of the image. Map
  // crop coordinates back to the source independently on each axis so the
  // exported bitmap cannot include a rounded edge of the editor background.
  return {
    width: Math.max(1, Math.round((box.width * image.width) / editor.width)),
    height: Math.max(1, Math.round((box.height * image.height) / editor.height)),
  }
}

const renderExportCanvas = (box: CropBox) => {
  const editor = canvas.value
  if (!editor) return null

  const size = getExportPixelSize(box)
  // Render Fabric at native resolution instead of painting the modal-sized
  // canvas and enlarging it afterwards. This keeps the source pixels and all
  // vector annotations sharp; any final resample is only a rounding correction.
  const multiplier = Math.max(size.width / box.width, size.height / box.height, 1)
  const source = editor.toCanvasElement(multiplier, {
    left: box.x,
    top: box.y,
    width: box.width,
    height: box.height,
    filter: (object: { get: (key: string) => unknown }) => !isCropUiObject(object),
  } as never)

  if (source.width === size.width && source.height === size.height) return source

  const output = document.createElement('canvas')
  output.width = size.width
  output.height = size.height
  const context = output.getContext('2d')
  if (!context) return source
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, 0, 0, source.width, source.height, 0, 0, output.width, output.height)
  return output
}

const canvasToBlob = (source: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => source.toBlob(resolve, type, quality))

const exportCanvasDataUrl = (format: 'png' | 'jpeg' = 'png') => {
  const box = getExportBox()
  if (!box) return ''
  const source = renderExportCanvas(box)
  return source?.toDataURL(`image/${format}`, 1) ?? ''
}

const exportDataUrl = () => {
  if (!cropBox.value) return ''
  return exportCanvasDataUrl()
}

const exportBlob = async (type = props.file.type || 'image/png', quality = 1) => {
  const box = getExportBox()
  if (!box) return null
  const source = renderExportCanvas(box)
  if (!source) return null
  const blob = await canvasToBlob(source, type, quality)
  if (!blob || blob.type === type) return blob
  return new Blob([blob], { type: blob.type || type })
}

const applyCrop = async () => {
  const editor = canvas.value
  if (!editor || !cropBox.value) return
  lastCropSourceDataUrl = exportCanvasDataUrl()
  const dataUrl = exportDataUrl()
  removeCropUi()
  await loadFlattenedImage(dataUrl)
  history.reset(editor)
  emitHistoryState()
}

const applyFilter = (filterName: string) => {
  const image = baseImage.value
  if (!image) return
  const filters = fabric.filters
  if (filterName === 'clear') {
    image.filters = []
  } else if (filterName === 'grayscale') {
    image.filters = [new filters.Grayscale()]
  } else if (filterName === 'sepia') {
    image.filters = [new filters.Sepia()]
  } else if (filterName === 'brightness') {
    image.filters = [new filters.Brightness({ brightness: 0.18 })]
  } else if (filterName === 'contrast') {
    image.filters = [new filters.Contrast({ contrast: 0.22 })]
  } else if (filterName === 'blur') {
    image.filters = [new filters.Blur({ blur: 0.18 })]
  }
  image.applyFilters()
  canvas.value?.requestRenderAll()
  if (canvas.value) history.save(canvas.value)
  emitHistoryState()
}

const getCensorFilter = (mode = props.censorMode, level = censorLevel.value) =>
  mode === 'pixelate'
    ? new fabric.filters.Pixelate({ blocksize: Math.round(2 + level * 20) })
    : new fabric.filters.Blur({ blur: level })

const applyCensorFilter = (mode = props.censorMode) => {
  const selectedMask = canvas.value?.getActiveObject()
  const censorId = selectedMask?.get('censorId') ?? censorMask.value?.get('censorId')
  const blurLayer = canvas.value?.getObjects().find(
    (object) => object.get('censorId') === censorId && isCensorBlurLayer(object, baseImage.value),
  ) as fabric.FabricImage | undefined
  if (!blurLayer) return
  const level = censorLevel.value
  blurLayer.set({ censorMode: mode, censorLevel: level })
  blurLayer.filters = [getCensorFilter(mode, level)]
  blurLayer.applyFilters()
  canvas.value?.requestRenderAll()
}

const censorSelection = async () => {
  const editor = canvas.value
  const image = baseImage.value
  const active = editor?.getActiveObject()
  if (!editor || !image) return

  const censorId = `censor-${++censorSequence}`
  const mode = props.censorMode
  const level = censorLevel.value

  const sourceObject = active && active !== image ? active : null
  const width = Math.min(sourceObject?.getScaledWidth() ?? 180, editor.width - 16)
  const height = Math.min(sourceObject?.getScaledHeight() ?? 96, editor.height - 16)
  const left = sourceObject?.left ?? Math.max((editor.width - width) / 2, 8)
  const top = sourceObject?.top ?? Math.max((editor.height - height) / 2, 8)

  const mask = new fabric.Rect({
    left,
    top,
    width: Math.max(width, 24),
    height: Math.max(height, 24),
    angle: sourceObject?.angle ?? 0,
    fill: 'rgba(255, 255, 255, 0.01)',
    stroke: 'transparent',
    strokeWidth: 0,
    name: 'censor-mask',
    isCensorMask: true,
    censorId,
    censorMode: mode,
    censorLevel: level,
    excludeFromExport: true,
  })
  const blurredImage = await image.clone()
  const clipPath = new fabric.Rect({
    left: mask.left,
    top: mask.top,
    width: mask.width,
    height: mask.height,
    angle: mask.angle,
    absolutePositioned: true,
  })

  blurredImage.set({
    selectable: false,
    evented: false,
    name: 'censor-blur',
    censorId,
    censorMode: mode,
    censorLevel: level,
    clipPath,
  })
  blurredImage.filters = [getCensorFilter(mode, level)]
  blurredImage.applyFilters()
  enableObjectControls(mask)
  editor.add(blurredImage)
  editor.add(mask)
  censorMask.value = mask
  censorBlurLayer.value = blurredImage
  editor.setActiveObject(mask)
  setActiveCanvasObject(mask)
  editor.requestRenderAll()
  history.save(editor)
  emitHistoryState()
}

const setCensorLevel = async (level: number) => {
  censorLevel.value = Math.min(Math.max(level, 0.05), 0.8)
  const mask = canvas.value?.getActiveObject()
  if (mask?.get('isCensorMask')) mask.set({ censorLevel: censorLevel.value })
  if (!censorBlurLayer.value) return
  applyCensorFilter()
  if (canvas.value) history.save(canvas.value)
  emitHistoryState()
}

const setCensorMode = (mode: 'blur' | 'pixelate') => {
  if (mode !== 'blur' && mode !== 'pixelate') return
  const mask = canvas.value?.getActiveObject()
  if (mask?.get('isCensorMask')) mask.set({ censorMode: mode })
  applyCensorFilter(mode)
  if (canvas.value) history.save(canvas.value)
  emitHistoryState()
}

const handleTool = (tool: string) => {
  const editor = canvas.value
  if (!editor) return
  editor.isDrawingMode = tool === 'draw'
  if (tool !== 'crop' && !cropBox.value) editor.uniformScaling = false
  if (tool === 'crop') void ensureCropRect()
  if (tool === 'rect') addRect()
  if (tool === 'circle') addCircle()
  if (tool === 'line') addLine()
  if (tool === 'arrow') addArrow()
  if (tool === 'censor') void censorSelection()
}

const undo = async () => {
  if (!canvas.value) return
  await history.undo(canvas.value)
  syncCanvasRefs()
  emitHistoryState()
}

const redo = async () => {
  if (!canvas.value) return
  await history.redo(canvas.value)
  syncCanvasRefs()
  emitHistoryState()
}

const discardLastChange = async () => {
  const editor = canvas.value
  if (!editor) return

  if (cropBox.value) {
    removeCropUi()
    editor.discardActiveObject()
    editor.requestRenderAll()
    emitHistoryState()
    return
  }

  await undo()
}

const applyChanges = async () => {
  const editor = canvas.value
  if (!editor) return

  if (cropBox.value) {
    await applyCrop()
    return
  }

  editor.isDrawingMode = false
  editor.discardActiveObject()
  editor.requestRenderAll()
  history.save(editor)
  emitHistoryState()
}

const exportFile = async () => {
  const editor = canvas.value
  if (!editor) return props.file
  let blob: Blob | null
  if (cropBox.value) {
    blob = await exportBlob(props.file.type || 'image/png', 1)
    removeCropUi()
  } else {
    blob = await exportBlob(props.file.type || 'image/png', 1)
  }
  if (!blob) return props.file
  return new File([blob], props.file.name, {
    type: blob.type || props.file.type || 'image/png',
    lastModified: Date.now(),
  })
}

const disposeCanvas = () => {
  window.removeEventListener('keydown', handleEditorKeyDown)
  isDeletingObject = false
  restoreTextMouseMove?.()
  stopActivePointerInteraction?.()
  stopActivePointerInteraction = null
  isTextEditing.value = false
  canvas.value?.dispose()
  canvas.value = undefined
  baseImage.value = undefined
  cropRect.value = undefined
  cropOverlay.value = []
  activeTextBox.value = null
  textBoxFrame.value = null
  activeShape.value = null
  shapeBoxFrame.value = null
  censorMask.value = null
  censorBlurLayer.value = null
  zoomLevel.value = 1
  if (objectUrl) URL.revokeObjectURL(objectUrl)
  objectUrl = ''
}

watch(
  () => props.file,
  () => nextTick(initializeCanvas),
)
watch(() => props.activeTool, handleTool)
watch(
  () => props.color,
  () => {
    setDrawingBrush()
    applyTextStyleToSelection()
  },
)
watch(() => props.brushWidth, setDrawingBrush)
watch(
  () => props.cropRatio,
  () => {
    applyCropRatio()
  },
)
watch(() => props.aspectRatio, applyCropRatio)
watch(() => props.fontSize, applyTextStyleToSelection)
watch(() => props.fontFamily, applyTextStyleToSelection)
watch(() => props.textAlign, applyTextStyleToSelection)

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  disposeCanvas()
})

onMounted(() => {
  if (!stageRef.value) return
  resizeObserver = new ResizeObserver(fitCanvasToStage)
  resizeObserver.observe(stageRef.value)
  void initializeCanvas()
})

defineExpose({
  undo,
  redo,
  exportFile,
  exportBlob,
  exportDataUrl,
  zoomIn,
  zoomOut,
  resetZoom,
  rotateLeft,
  rotateRight,
  undoCrop,
  applyFilter,
  applyCrop,
  applyChanges,
  discardLastChange,
  addText,
  addEmoji,
  setCensorLevel,
  setCensorMode,
  deleteActiveObject,
  disposeCanvas,
})
</script>

<style scoped>
.text-editing-cursor {
  cursor: auto !important;
}
</style>
